import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../slice/authSlice";
import "../css/TopNav.css";

// Each link decides for itself which URLs make it "active".
// Candidates lives at /dashboard (the list) and /dashboard/candidates/...
// (detail + edit). It must NOT match /dashboard/clients or
// /dashboard/optimizations, which is why a plain "starts with /dashboard/"
// check can't be used for it.
const NAV_LINKS = [
  {
    to: "/dashboard",
    label: "Candidates",
    isActive: (path) => path === "/dashboard" || path.startsWith("/dashboard/candidates"),
  },
  {
    to: "/dashboard/clients",
    label: "Clients",
    isActive: (path) => path.startsWith("/dashboard/clients"),
  },
  {
    to: "/dashboard/optimizations",
    label: "Optimizations",
    isActive: (path) => path.startsWith("/dashboard/optimizations"),
  },
];

// Readable role names instead of raw codes like "TEAM_LEAD".
const ROLE_LABELS = { ADMIN: "Admin", TEAM_LEAD: "Team lead", RECRUITER: "Recruiter" };
const roleLabel = (role) => {
  if (!role) return "";
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  const text = role.toLowerCase().replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

export default function TopNav() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    await dispatch(logoutUser());
    navigate("/login");
  };

  const pathname = location.pathname;

  const isAdmin = user?.role === "ADMIN";

  const links = [
    ...NAV_LINKS,
    // Same label for everyone; admins land on the team-wide dashboard,
    // recruiters / team leads on their own list.
    {
      to: isAdmin ? "/admin/screenings" : "/dashboard/screenings",
      label: "AI Screenings",
      isActive: (path) => path.startsWith("/dashboard/screenings") || path.startsWith("/admin/screenings"),
    },
    ...(isAdmin
      ? [{ to: "/admin", label: "Admin panel", isActive: (path) => path === "/admin" || path === "/admin/" }]
      : []),
  ];

  return (
    <nav className="hp-nav" aria-label="Main">
      <div className="hp-nav-inner">
        <Link to="/dashboard" className="hp-brand" aria-label="LoneWolf home">
          <span className="" aria-hidden="true">
           <img src="/logo192.png" alt="" className="hp-logo" width="32" height="32" />
          </span>
          <span className="hp-brand-name">LoneWolf</span>
        </Link>

        <div className="hp-links">
          {links.map((link) => {
            const active = link.isActive(pathname);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`hp-link${active ? " is-active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="hp-user">
          <span className="hp-avatar" aria-hidden="true">
            {initials(user?.name)}
          </span>
          <div className="hp-who">
            <span className="hp-who-name">{user?.name}</span>
            <span className="hp-who-role">{roleLabel(user?.role)}</span>
          </div>
          <span className="hp-sep" aria-hidden="true" />
          <button type="button" className="hp-logout" onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? "Logging out…" : "Log out"}
          </button>
        </div>
      </div>
    </nav>
  );
}